import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { FiArrowUpRight } from 'react-icons/fi';
import { nav, profile } from '../data/profile';
import useDubaiTime from '../hooks/useDubaiTime';
import './Nav.css';

export default function Nav({ ready }) {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const time = useDubaiTime(false);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(latest > 24);
    if (open) return;
    if (latest > prev && latest > 160) setHidden(true);
    else setHidden(false);
  });

  useEffect(() => {
    document.documentElement.style.overflow = open ? 'hidden' : '';
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [open]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <motion.header
        className={`nav ${scrolled ? 'nav--scrolled' : ''}`}
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: hidden ? -100 : 0, opacity: 1 } : { y: -80, opacity: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="container nav__inner">
          <a href="#top" className="nav__brand" onClick={go('top')} aria-label="Back to top">
            <span className="nav__brand-name">Ayaan Ahmad</span>
            <span className="nav__brand-role mono">Backend &amp; AI</span>
          </a>

          <nav className="nav__links" aria-label="Primary">
            {nav.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="nav__link" onClick={go(item.id)}>
                <span>{item.label}</span>
              </a>
            ))}
          </nav>

          <div className="nav__meta">
            <span className="nav__time mono" title="Local time in Dubai">
              <span className="dot-live" /> Dubai {time}
            </span>
            <a href={profile.links.email} className="nav__cta">
              Say hello <FiArrowUpRight />
            </a>
          </div>

          <button
            className={`nav__burger ${open ? 'is-open' : ''}`}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            <span />
            <span />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="menu"
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="container menu__inner">
              <ul className="menu__list">
                {nav.map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }}
                    transition={{ delay: 0.25 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <a href={`#${item.id}`} onClick={go(item.id)}>
                      <span className="menu__index mono">0{i + 1}</span>
                      <span className="menu__label">{item.label}</span>
                    </a>
                  </motion.li>
                ))}
              </ul>
              <motion.div
                className="menu__foot"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.5 }}
              >
                <a href={profile.links.email} className="link">{profile.email}</a>
                <div className="menu__social">
                  <a href={profile.links.linkedin} target="_blank" rel="noreferrer" className="link link--quiet">LinkedIn</a>
                  <a href={profile.links.github} target="_blank" rel="noreferrer" className="link link--quiet">GitHub</a>
                  <a href={profile.links.devto} target="_blank" rel="noreferrer" className="link link--quiet">dev.to</a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
